import { BuildClass, DateObj, DateTimeObj, Do, fsmData } from '../../../universal';
import { React, _ } from '../../lib';
import { Checkbox } from './checkbox';
import { setContextMenu } from './context-menu';
import { HelpIcon } from './help';
import { List } from './list';
import { getInternalValue } from './list-util';
import { ConditionalObject, useRSInstance, useStateSync, } from './meta-types';
import { stubListInstance } from './stubs';
import { TextSearched, searchText } from './text';
const ListGridComponent = (props, ref) => {
    const rs = useRSInstance({
        props: props,
        refs: {
            root: React.useRef(null),
            optionsList: React.useRef(stubListInstance),
            colorScale: React.useRef([0, 0]),
            needles: React.useRef([]),
        },
        defaultState: React.useCallback(props => ({
            value: getInternalValue(props.value).map(String),
            view: getViewFromProps(props, props.view),
            grouping: getGroupingStateDefault(props, props.view?.groupingField ?? null, props.view?.groupingFieldRev ?? null),
        }), []),
        actionToDelta: getPartialStateFromAction,
    });
    const updateListValue = React.useCallback((v) => {
        rs.dispatch([Action.UpdateSelectedValue, { value: getInternalValue(v) }]);
    }, []);
    rs.refs.needles.current = (rs.props.searchText ?? '').split(/\s+/).filter(x => x);
    useStateSync({
        propVal: props.value,
        stateVal: rs.state.value,
        setProp: v => {
            const vals = v.map(x => convertInternalValueToProp(props, x));
            const pVals = props.multiple ? vals : _.first(vals);
            props.onUpdate(pVals);
        },
        setState: v => {
            rs.dispatch([
                Action.UpdateSelectedValue,
                { value: getInternalValue(v).map(String) },
            ]);
        },
        compareFn: _.isEqual,
    });
    useStateSync({
        propVal: getViewFromProps(rs.props, rs.props.view),
        stateVal: rs.state.view,
        setProp: v => rs.props.onUpdateView?.(v),
        setState: v => {
            updateView(rs, () => v);
        },
        disabled: !rs.props.view || !rs.props.onUpdateView,
    });
    rs.refs.colorScale.current = React.useMemo(() => cacheColorScale(props.fields, props.data, rs.state.view.colorBy), [props.fields, props.data, rs.state.view.colorBy]);
    React.useImperativeHandle(ref, () => ({
        getElement: () => rs.refs.root.current,
        getListElement: () => rs.refs.optionsList.current.getElement(),
        getFullTable: () => getFullTable(rs),
        getFilteredRecords: (searchTextOverride) => getFilteredRecords(rs, searchTextOverride),
        getHiddenFields: () => rs.state.view.hiddenFields,
        getList: () => rs.refs.optionsList.current,
        focus: () => {
            rs.refs.optionsList.current.focus();
        },
    }));
    return (React.createElement("div", { className: BuildClass({
            'ui5-listgrid': true,
            [props.className]: true,
            'checkbox-selection': props.checkboxSelection,
        }), style: props.style },
        buildHeadingRow(rs),
        React.createElement(List, { value: props.multiple ? rs.state.value : _.first(rs.state.value), ref: rs.refs.optionsList, onUpdate: updateListValue, multiple: props.multiple, multipleDefault: props.multipleDefault, readOnly: props.readOnly, height: props.height?.(null) ?? 26, rows: _.compact(buildItems(rs)), disableFocusControl: props.disableFocusControl, disableClickHandlers: props.disableClickHandlers, onKeyDown: props.onKeyDown, disableLazyRendering: props.disableLazyRendering })));
};
const ListGridStaticComponent = (props, ref) => (React.createElement(ListGrid, { ref: ref, value: null, onUpdate: _.noop, readOnly: true, selectable: () => false, multiple: false, multipleDefault: false, checkboxSelection: false, disableFocusControl: true, sortableItems: null, ...props }));
const buildHeadingRow = (rs) => {
    const fieldsBySubRow = _.groupBy(rs.props.fields, F => F.row ?? 1);
    const subRowKeys = fsmData(_.keys(fieldsBySubRow), {
        sort: x => +x,
        map: x => +x,
    });
    return (React.createElement("div", { className: BuildClass({
            heading: true,
            [rs.props.classRow?.(null)]: true,
        }) }, subRowKeys.map(sr => (React.createElement("div", { key: sr, className: BuildClass({
            heading: true,
            'sub-row': true,
            [`sub-row-${sr}`]: true,
        }) },
        ConditionalObject(sr == 1 && rs.props.checkboxSelection, () => (React.createElement("span", { key: "checkbox-selection", className: "checkbox-selection cell noselect" },
            React.createElement(Checkbox, { lbl: "", value: Do(() => {
                    const values = _.sortBy(rs.props.data.map(x => rs.props.pk(x)));
                    return _.isEqual(values, _.sortBy(rs.state.value));
                }), onClick: e => {
                    e.stopPropagation();
                }, onUpdate: v => {
                    updateState(rs, () => ({
                        value: !v
                            ? []
                            : rs.props.data.map(x => String(rs.props.pk(x))),
                    }));
                } })))),
        React.createElement(React.Fragment, null, fsmData(rs.props.fields, {
            filter: F => (F.row ?? 1) == sr &&
                !(F.disabled ?? false) &&
                !rs.state.view.hiddenFields.includes(F.key),
            map: F => buildHeadingCell(rs, F),
        })))))));
};
const buildHeadingCell = (rs, F) => {
    const key = F.key;
    let lblDesc = F.lblDesc ?? F.lbl;
    let { lbl } = F;
    const sortable = (rs.props.sortable ?? true) &&
        (F.sortVal != null || F.text != null) &&
        (F.sortable ?? true);
    if (rs.state.view.columnFilters[key] != null) {
        lblDesc += ' (filtered by value)';
        lbl += '*';
    }
    const view = rs.state.view;
    return (React.createElement("span", { key: key, className: BuildClass({
            [(key ?? '').toLowerCase().replace(/[^a-z0-9]/g, '') ?? '']: true,
            cell: true,
            [rs.props.classCell?.(null, F) ?? '']: true,
            [F.className?.(null) ?? '']: true,
            'help-indicator': Boolean(F.tooltipBubble),
            filtered: view.columnFilters?.[key] != null,
            nosort: !sortable,
            sorted: sortable && key === view.sortKey,
            'sort-asc': sortable && key === view.sortKey && !view.sortRev,
            'sort-dsc': sortable && key === view.sortKey && view.sortRev,
            grouped: key === view.groupingField,
            'group-asc': key === view.groupingField && !rs.state.grouping.reverse,
            'group-dsc': key === view.groupingField && rs.state.grouping.reverse,
        }), style: Do(() => {
            const s = {};
            if (F.width?.init != null) {
                s.width = F.width.init;
                s.maxWidth = F.width.init;
                s.minWidth = F.width.init;
            }
            if (F.width?.flex != null) {
                s.flex = F.width.flex;
            }
            return s;
        }), title: !F.tooltipBubble ? lblDesc : undefined, onClick: Do(() => {
            if (key === view.groupingField) {
                return () => {
                    updateState(rs, s => ({
                        grouping: {
                            ...s.grouping,
                            reverse: !s.grouping.reverse,
                        },
                    }));
                };
            }
            else if (sortable) {
                return () => {
                    updateView(rs, s => ({
                        sortKey: key,
                        sortRev: Do(() => {
                            if (s.sortKey === key) {
                                return !s.sortRev;
                            }
                            return F.sortReverseDefault ?? false;
                        }),
                    }));
                };
            }
            return undefined;
        }), onMouseDown: e => {
            if (e.button === 1) {
                e.stopPropagation();
                e.preventDefault();
                updateGroups(rs, s => getGroupingDelta(rs, {
                    groupingField: key !== s.view.groupingField ? key : null,
                    groupingFieldRev: key !== s.view.groupingField ? false : null,
                }));
                return;
            }
        }, onContextMenu: e => {
            e.preventDefault();
            headingCellContextMenu(rs, e, F);
        } },
        ConditionalObject(!F.tooltipBubble, lbl),
        ConditionalObject(Boolean(F.tooltipBubble), React.createElement(React.Fragment, null,
            React.createElement("div", { key: "lbl", title: lblDesc }, lbl),
            React.createElement(HelpIcon, { key: "help-tooltip", title: lblDesc })))));
};
const buildItems = (rs) => {
    const groups = getGroupedRecords(rs);
    const grouping = rs.state.grouping ?? rs.props.grouping;
    return _.flatMap(groups, group => {
        const heading_gap = (groups.length > 1 || group.ID != null) && (grouping.gaps ?? true);
        const heading_row = (groups.length > 1 || group.ID != null) && group.Name != null;
        let total_rows = group.Records.length;
        total_rows += heading_gap ? 1 : 0;
        total_rows += heading_row ? 1 : 0;
        return [
            Do(() => {
                if (heading_gap) {
                    return {
                        height: group.ID === groups[0].ID ? 0 : null,
                        value: `listgrid-group-gap-${group.ID}`,
                        selectable: false,
                        className: 'no-odd',
                        content: () => (React.createElement("div", { className: BuildClass({
                                row: true,
                                'listgrid-group-heading-gap': true,
                                'no-select-highlight': true,
                                'hidden-grouping-row': group.ID === groups[0].ID,
                                [rs.props.classRow?.(null) ?? '']: true,
                            }) },
                            React.createElement("span", null, "\u00A0"))),
                        contentArray: () => [''],
                    };
                }
                else if (heading_row) {
                    return {
                        height: 0,
                        value: `listgrid-group-mod21-${group.ID}`,
                        selectable: false,
                        className: 'no-odd',
                        content: () => React.createElement("div", { className: "row hidden-grouping-row" }),
                        contentArray: () => [''],
                    };
                }
                return null;
            }),
            ConditionalObject(heading_row, () => buildGroupHeadingRow(rs, group)),
            ...buildItemsInnerGroup(rs, group.Records),
            ConditionalObject(heading_row && groups.length > 1 && total_rows % 2 != 0, {
                height: 0,
                value: `listgrid-group-mod22-${group.ID}`,
                selectable: false,
                className: 'no-odd',
                content: () => React.createElement("div", { className: "row hidden-grouping-row" }),
                contentArray: () => [''],
            }),
        ];
    });
};
const buildItemsInnerGroup = (rs, records) => {
    const sortingFunctions = Do(() => {
        const userSortable = rs.props.sortable ?? true;
        const sortKey = userSortable ? rs.state.view.sortKey : rs.props.defaultSortingKey;
        let sortFn = null;
        let preSort = null;
        _.forEach(rs.props.fields, F => {
            if (F.key == sortKey) {
                preSort = F.preSort;
                sortFn =
                    F.sortVal ??
                        F.text ??
                        (() => {
                            console.warn('No sort value found for key: ', F.key);
                            return null;
                        });
                return false;
            }
            return true;
        });
        if (sortFn != null) {
            return _.compact([preSort, sortFn]);
        }
        console.warn('No sorting value found for key: ', sortKey);
        return [() => 1];
    });
    return fsmData(records, {
        sort: sortingFunctions.map(fn => record => fn(record, rs.state.view.sortRev)),
        reverse: rs.state.view.sortRev && (rs.props.sortable ?? true),
        map: x => ({
            height: rs.props.height?.(x),
            selectable: (rs.props.selectable ?? (() => true))(x),
            value: String(rs.props.pk(x)),
            content: () => buildItem(rs, x),
            contentArray: () => buildItemContentArray(rs, x),
        }),
    });
};
const buildGroupHeadingRow = (rs, group) => ({
    value: `listgrid-group-${group.ID}`,
    selectable: false,
    className: 'no-odd',
    contentArray: () => [group.Name],
    content: () => (React.createElement("div", { className: BuildClass({
            row: true,
            'listgrid-group-heading': true,
            'no-select-highlight': true,
            [(rs.props.classRow ?? (() => ''))(null)]: true,
        }), title: `${group.Name} - ${group.Records.length}` }, group.Name)),
});
const formatText = (text) => {
    if (!text) {
        return '';
    }
    if (text instanceof DateObj || text instanceof DateTimeObj) {
        return text.fmt();
    }
    return String(text);
};
const buildItemContentArray = (rs, record) => fsmData(rs.props.fields, {
    filter: F => !(F.disabled ?? false) && !rs.state.view.hiddenFields.includes(F.key),
    map: F => formatText(F.text(record)),
});
const buildItem = (rs, record) => {
    const fieldsBySubRow = _.groupBy(rs.props.fields, F => F.row ?? 1);
    const subRowKeys = fsmData(_.keys(fieldsBySubRow), {
        sort: x => +x,
        map: x => +x,
    });
    const selectable = (rs.props.selectable ?? (() => true))(record);
    const value = String(rs.props.pk(record));
    return (React.createElement("div", { className: BuildClass({
            row: true,
            [(rs.props.classRow ?? (() => null))(record)]: true,
        }), title: rs.props.tooltipRow?.(record) ?? '', onClick: ev => rs.props.onClick?.(record, ev), onContextMenu: ev => rs.props.onContextMenu?.(record, ev), onDoubleClick: ev => rs.props.onDoubleClick?.(record, ev) }, subRowKeys.map(sr => (React.createElement("div", { key: sr, className: BuildClass({
            'sub-row': true,
            [`sub-row-${sr}`]: true,
        }) },
        ConditionalObject(sr == 1 && rs.props.checkboxSelection, () => (React.createElement("span", { className: "checkbox-selection cell" }, ConditionalObject(selectable, React.createElement(Checkbox, { lbl: "", readOnly: true, value: _.includes(rs.state.value, value), onUpdate: _.noop }))))),
        React.createElement(React.Fragment, null, fsmData(rs.props.fields, {
            filter: F => (F.row ?? 1) == sr &&
                !(F.disabled ?? false) &&
                !rs.state.view.hiddenFields.includes(F.key),
            map: F => (React.createElement("span", { key: F.key, style: Do(() => {
                    const s = buildColorStyle(rs, F, record) ?? {};
                    if (F.width?.init != null) {
                        s.width = F.width.init;
                        s.maxWidth = F.width.init;
                        s.minWidth = F.width.init;
                    }
                    if (F.width?.flex != null) {
                        s.flex = F.width.flex;
                    }
                    return s;
                }), className: BuildClass({
                    cell: true,
                    [(F.key ?? '')
                        .toLowerCase()
                        .replace(/[^a-z0-9]/g, '')]: true,
                    [rs.props.classCell?.(record, F)]: true,
                    [F.className?.(record)]: F.className != null,
                }), title: Do(() => {
                    if (F.tooltip != null) {
                        return F.tooltip(record);
                    }
                    return formatText(F.text(record));
                }), onClick: Do(() => {
                    if (F.onClick != null) {
                        return e => {
                            F.onClick(record, e);
                        };
                    }
                    return undefined;
                }) }, buildCell(rs, F, record))),
        })))))));
};
const buildColorStyle = (rs, field, record) => {
    if (rs.state.view.colorBy == null || rs.state.view.colorBy !== field.key) {
        return null;
    }
    let val = (field.colorVal ?? field.sortVal ?? field.text)?.(record);
    if (val == null || isNaN(+val)) {
        return null;
    }
    val = +val;
    const minMaxRange = field.colorRange ?? {
        low: rs.refs.colorScale.current[0],
        high: rs.refs.colorScale.current[1],
    };
    const scale = minMaxRange.high - minMaxRange.low;
    const proportion = (val - minMaxRange.low) / scale;
    const hsl_high = field.colorScale?.high ?? { h: 120, s: 100, l: 70 };
    const hsl_low = field.colorScale?.low ?? { h: 0, s: 100, l: 75 };
    const h = proportion * (hsl_high.h - hsl_low.h) + hsl_low.h;
    const s = proportion * (hsl_high.s - hsl_low.s) + hsl_low.s;
    const l = proportion * (hsl_high.l - hsl_low.l) + hsl_low.l;
    return { background: `hsl(${h},${s}%,${l}%)` };
};
const buildCell = (rs, field, record) => {
    if (field.display) {
        return React.createElement(React.Fragment, null, field.display(record));
    }
    const text = formatText(field.text(record));
    return React.createElement(TextSearched, { text: text, needles: rs.refs.needles.current });
};
const headingCellContextMenu = (rs, ev, field) => {
    setContextMenu({
        position: {
            x: ev.clientX,
            y: ev.clientY,
        },
        items: _.compact([
            {
                label: 'Reset view',
                icon: '/static/img/i8/material-outline-clear-filters.svg',
                onClick: () => {
                    updateState(rs, () => ({
                        grouping: null,
                        view: getViewFromProps(rs.props, rs.props.view),
                    }));
                },
            },
            {
                label: 'Copy as text',
                icon: '/static/img/i8/material-outline-copy.svg',
                onClick: () => {
                    const lines = _.map(getFullTable(rs), x => x.join('\t'));
                    navigator.clipboard.writeText(lines.join('\n'));
                },
            },
            (rs.props.sortable ?? true) ? '---' : undefined,
            {
                label: 'Sort ascending',
                shortcut: 'LMB',
                shortcutDesc: 'Left mouse button',
                icon: '/static/img/i8/material-outline-ascending-sorting.svg',
                hidden: !(rs.props.sortable ?? true),
                onClick: () => {
                    updateView(rs, () => ({
                        sortKey: field.key,
                        sortRev: false,
                    }));
                },
            },
            {
                label: 'Sort descending',
                icon: '/static/img/i8/material-outline-descending-sorting.svg',
                hidden: !(rs.props.sortable ?? true),
                onClick: () => {
                    updateView(rs, () => ({
                        sortKey: field.key,
                        sortRev: true,
                    }));
                },
            },
            buildContextMenuSortBy(rs),
            (rs.props.sortable ?? true) ? '---' : undefined,
            {
                label: 'Group ascending',
                shortcut: 'MMB',
                shortcutDesc: 'Middle mouse button',
                icon: '/static/img/i8/material-outline-ascending-sorting.svg',
                hidden: !(rs.props.sortable ?? true),
                onClick: () => {
                    updateGroups(rs, () => getGroupingDelta(rs, {
                        groupingField: field.key,
                        groupingFieldRev: false,
                    }));
                },
            },
            {
                label: 'Group descending',
                icon: '/static/img/i8/material-outline-descending-sorting.svg',
                hidden: !(rs.props.sortable ?? true),
                onClick: () => {
                    updateGroups(rs, () => getGroupingDelta(rs, {
                        groupingField: field.key,
                        groupingFieldRev: true,
                    }));
                },
            },
            buildContextMenuGroupBy(rs),
            '---',
            buildContextMenuFilterByValue(rs, field),
            {
                label: 'Hide column',
                icon: '/static/img/i8/material-outline-closed-eye.svg',
                onClick: () => {
                    updateView(rs, s => ({
                        hiddenFields: s.hiddenFields.concat(field.key),
                    }));
                },
            },
            {
                label: 'Toggle columns...',
                icon: '/static/img/i8/material-outline-delete-column.svg',
                items: [
                    {
                        label: 'Show All',
                        dontClose: true,
                        icon: () => {
                            if (rs.state.view.hiddenFields.length === 0) {
                                return '/static/img/i8/material-outline-eye.svg';
                            }
                            return '';
                        },
                        onClick: () => {
                            updateView(rs, s => ({
                                hiddenFields: Do(() => {
                                    if (s.hiddenFields.length === 0) {
                                        return rs.props.fields.map(F => F.key);
                                    }
                                    return [];
                                }),
                            }));
                        },
                    },
                    '---',
                    ...fsmData(rs.props.fields, {
                        filter: F => !(F.disabled ?? false),
                        map: F => ({
                            label: F.lblLong ?? F.lbl,
                            labelDesc: F.lblDesc ?? F.lbl,
                            icon: () => {
                                if (rs.state.view.hiddenFields.includes(F.key)) {
                                    return null;
                                }
                                return '/static/img/i8/material-outline-eye.svg';
                            },
                            dontClose: true,
                            onClick: () => {
                                updateView(rs, s => ({
                                    hiddenFields: Do(() => {
                                        if (s.hiddenFields.includes(F.key)) {
                                            return s.hiddenFields.filter(x => F.key !== x);
                                        }
                                        return s.hiddenFields.concat(F.key);
                                    }),
                                }));
                            },
                        }),
                    }),
                ],
            },
            '---',
            {
                label: 'Colour by value',
                icon: '/static/img/i8/material-outline-paint-palette.svg',
                onClick: () => {
                    updateView(rs, s => ({
                        colorBy: s.colorBy !== field.key ? field.key : null,
                    }));
                },
            },
        ]),
    });
};
const buildContextMenuSortBy = (rs) => ({
    label: 'Sort By...',
    icon: '/static/img/i8/material-outline-sort.svg',
    hidden: !(rs.props.sortable ?? true),
    items: fsmData(rs.props.fields, {
        filter: F => (F.sortVal != null || F.text != null) && (F.sortable ?? true),
        map: F => ({
            label: F.lblLong ?? F.lbl,
            labelDesc: F.lblDesc ?? F.lbl,
            dontClose: true,
            icon: () => {
                if (rs.state.view.sortKey !== F.key) {
                    return null;
                }
                else if (rs.state.view.sortRev) {
                    return '/static/img/i8/material-outline-descending-sorting.svg';
                }
                return '/static/img/i8/material-outline-ascending-sorting.svg';
            },
            onClick: () => {
                updateView(rs, s => ({
                    sortKey: F.key,
                    sortRev: Do(() => {
                        if (s.sortKey === F.key) {
                            return !s.sortRev;
                        }
                        return F.sortReverseDefault ?? false;
                    }),
                }));
            },
        }),
    }),
});
const buildContextMenuGroupBy = (rs) => ({
    label: 'Group by...',
    icon: '/static/img/i8/material-outline-group-objects.svg',
    hidden: !(rs.props.sortable ?? true),
    items: [
        {
            label: 'None',
            dontClose: true,
            icon: () => {
                if (rs.state.view.groupingField != null) {
                    return null;
                }
                if (rs.props.grouping == null && rs.state.grouping != null) {
                    return null;
                }
                if (rs.props.grouping != null && rs.state.grouping == null) {
                    return null;
                }
                return '/static/img/i8/material-outline-checkmark.svg';
            },
            onClick: () => {
                if (rs.props.grouping == null) {
                    updateGroups(rs, () => getGroupingDelta(rs, {
                        groupingField: null,
                        groupingFieldRev: null,
                        grouping: null,
                    }));
                }
                else {
                    updateGroups(rs, () => getGroupingDelta(rs, {
                        groupingField: null,
                        groupingFieldRev: null,
                        grouping: {
                            key: () => 1,
                            name: () => null,
                            sort: () => 1,
                            reverse: false,
                            gaps: false,
                        },
                    }));
                }
            },
        },
        {
            label: 'Default',
            hidden: rs.props.grouping == null,
            icon: () => {
                if (rs.state.grouping == null) {
                    return '/static/img/i8/material-outline-checkmark.svg';
                }
                return '';
            },
            dontClose: true,
            onClick: () => {
                updateGroups(rs, () => getGroupingDelta(rs, {
                    groupingField: null,
                    groupingFieldRev: null,
                    grouping: null,
                }));
            },
        },
        '---',
        ...fsmData(rs.props.fields, {
            filter: F => (F.groupVal ?? F.text) != null && !(F.disabled ?? false),
            map: F => ({
                label: F.lblLong ?? F.lbl,
                labelDesc: F.lblDesc ?? F.lbl,
                icon: () => {
                    if (rs.state.view.groupingField !== F.key) {
                        return null;
                    }
                    else if (rs.state.grouping.reverse) {
                        return '/static/img/i8/material-outline-descending-sorting.svg';
                    }
                    return '/static/img/i8/material-outline-ascending-sorting.svg';
                },
                dontClose: true,
                onClick: () => {
                    updateGroups(rs, s => getGroupingDelta(rs, {
                        groupingField: F.key,
                        groupingFieldRev: s.view.groupingField === F.key
                            ? !s.view.groupingFieldRev
                            : false,
                    }));
                },
            }),
        }),
    ],
});
const buildContextMenuFilterByValue = (rs, field) => ({
    label: 'Filter column',
    icon: '/static/img/i8/material-outline-filter.svg',
    hidden: field.text == null,
    items: () => {
        const values_fixed = field.filterValues?.() ?? [];
        let values_found = _.uniq(fsmData(rs.props.data, {
            sort: x => (field.sortVal ?? field.text)?.(x) ?? null,
            map: x => (field.filterVal?.getKey ?? field.text)?.(x) ?? null,
        }));
        values_found = _.filter(values_found, x => !values_fixed.includes(x));
        const values = _.uniq(_.concat(values_found, values_fixed));
        let existing = rs.state.view.columnFilters[field.key] ?? null;
        const tick = '/static/img/i8/material-outline-checkmark.svg';
        return [
            {
                label: 'Select All',
                icon: existing == null ? tick : undefined,
                dontClose: true,
                onClick: () => {
                    updateView(rs, s => {
                        const c = _.clone(s.columnFilters);
                        const k = field.key;
                        c[k] = c[k] != null ? null : [];
                        return { columnFilters: c };
                    });
                },
            },
            '---',
            ..._.map(values, v => ({
                label: (field.filterVal?.getText ?? String)(v),
                icon: existing == null || existing.includes(v) ? tick : undefined,
                dontClose: true,
                onClick: () => {
                    updateView(rs, s => {
                        const c = _.clone(s.columnFilters);
                        existing = c[field.key];
                        if (existing == null) {
                            existing = values.filter(x => x !== v);
                        }
                        else if (existing.includes(v)) {
                            existing = existing.filter(x => x !== v);
                        }
                        else {
                            existing = existing.concat(v);
                        }
                        if (_.difference(values, existing).length === 0) {
                            existing = null;
                        }
                        c[field.key] = existing;
                        return { columnFilters: c };
                    });
                },
            })),
        ];
    },
});
const getViewFromProps = (props, view) => ({
    colorBy: view?.colorBy ?? null,
    sortKey: view?.sortKey ?? props.defaultSortingKey,
    sortRev: view?.sortRev ?? props.defaultSortingReverse ?? false,
    hiddenFields: view?.hiddenFields ?? [],
    columnFilters: view?.columnFilters ?? {},
    groupingField: view?.groupingField ?? null,
    groupingFieldRev: view?.groupingFieldRev ?? null,
});
const getGroupedRecords = (rs) => {
    const grouping = rs.state.grouping ?? rs.props.grouping;
    const records = getFilteredRecords(rs);
    const groups = _.groupBy(records, x => JSON.stringify(grouping?.key?.(x) ?? null));
    const getGroupObj = (k) => {
        const items = groups[JSON.stringify(k)];
        return {
            ID: k,
            Name: grouping?.name?.(k, items[0]) ?? null,
            Records: items,
        };
    };
    let group_keys = _.keys(groups).map(x => JSON.parse(x));
    group_keys = _.sortBy(group_keys, gk => {
        const fn = grouping?.sort ?? (x => x.Name);
        return fn(getGroupObj(gk));
    });
    if (grouping?.reverse) {
        _.reverse(group_keys);
    }
    return _.map(group_keys, x => getGroupObj(x));
};
const getFilteredRecords = (rs, searchTextOverride) => {
    let needle = rs.refs.needles.current;
    if (searchTextOverride != null) {
        needle = searchTextOverride.split(/\s+/).filter(x => x);
    }
    const filterSearchText = Do(() => {
        if (needle.length == 0) {
            return () => true;
        }
        const textFns = fsmData(rs.props.fields, {
            filter: F => F.text != null && (F.searchable ?? true) && !(F.disabled ?? false),
            map: F => (x) => formatText(F.text(x)).toLowerCase(),
        });
        return (record) => {
            let found = false;
            _.forEach(textFns, fn => {
                if (searchText(fn(record), needle, true)) {
                    found = true;
                    return false;
                }
                return true;
            });
            return found;
        };
    });
    const filterColumnValues = Do(() => {
        const cols = fsmData(rs.props.fields, {
            filter: F => {
                const values = rs.state.view.columnFilters[F.key];
                return values != null && F.text != null;
            },
            map: F => ({
                getValue: (record) => (F.filterVal?.getKey ?? F.text)?.(record) ?? null,
                values: rs.state.view.columnFilters[F.key],
            }),
        });
        if (cols.length === 0) {
            return () => true;
        }
        return (record) => {
            let failed = false;
            _.forEach(cols, x => {
                const needle = x.getValue(record);
                if (!x.values.includes(needle)) {
                    failed = true;
                    return false;
                }
                return true;
            });
            return !failed;
        };
    });
    return _.filter(rs.props.data, record => {
        if (!filterSearchText(record)) {
            return false;
        }
        if (!filterColumnValues(record)) {
            return false;
        }
        return true;
    });
};
const getGroupingDelta = (rs, group) => {
    if (!(rs.props.sortable ?? true)) {
        return {
            groupingField: null,
            groupingFieldRev: null,
            grouping: null,
        };
    }
    return {
        groupingField: group.groupingField,
        groupingFieldRev: group.groupingFieldRev,
        grouping: group.grouping ??
            getGroupingStateDefault(rs.props, group.groupingField, group.groupingFieldRev),
    };
};
const getGroupingStateDefault = (props, groupingField, groupingFieldRev) => {
    const F = fsmData(props.fields, {
        filter: F => groupingField === F.key,
        takeFirst: true,
    });
    if (F == null) {
        return null;
    }
    const keyFn = F.groupVal ?? F.sortVal ?? F.text;
    if (keyFn == null) {
        return {
            key: () => 1,
            name: () => null,
            sort: () => 1,
            reverse: false,
            gaps: false,
        };
    }
    return {
        key: keyFn,
        name: F.groupName ?? ((_k, x) => formatText(F.text(x))),
        sort: F.groupSort ?? (x => (F.sortVal ?? F.text)(x.Records[0])),
        reverse: groupingFieldRev,
        gaps: false,
    };
};
const getFullTable = (rs) => {
    const heading_row = fsmData(rs.props.fields, {
        filter: F => !(F.disabled ?? false) && !rs.state.view.hiddenFields.includes(F.key),
        map: F => String(F.lbl),
    });
    const rows = _.compact(buildItems(rs)).map((x) => {
        const rowFn = x?.getItem != null ? x.getItem : x;
        const row = _.isFunction(rowFn) ? rowFn() : rowFn;
        return row.contentArray();
    });
    return [heading_row, ...rows];
};
const cacheColorScale = (propsFields, propsData, stateColorBy) => {
    if (stateColorBy == null) {
        return [null, null];
    }
    const fn = fsmData(propsFields, {
        filter: F => stateColorBy === F.key,
        map: F => F?.colorVal ?? F?.sortVal ?? F?.text ?? _.noop,
        takeFirst: true,
    });
    const vals = _.map(propsData, x => +fn(x) || null);
    return [_.min(vals) ?? null, _.max(vals) ?? null];
};
const convertInternalValueToProp = (props, val) => {
    let isNumeric = false;
    const record = props.data[0];
    if (record) {
        isNumeric = _.isNumber(props.pk(record));
    }
    return isNumeric ? +val : val;
};
const updateState = (rs, delta) => {
    rs.dispatch([Action.UpdateState, delta]);
};
const updateView = (rs, delta) => {
    rs.dispatch([Action.UpdateView, delta]);
};
const updateGroups = (rs, delta) => {
    rs.dispatch([Action.UpdateGrouping, delta]);
};
var Action;
(function (Action) {
    Action[Action["UpdateState"] = 0] = "UpdateState";
    Action[Action["UpdateView"] = 1] = "UpdateView";
    Action[Action["UpdateGrouping"] = 2] = "UpdateGrouping";
    Action[Action["UpdateSelectedValue"] = 3] = "UpdateSelectedValue";
})(Action || (Action = {}));
const getPartialStateFromAction = (rs, p) => {
    switch (p[0]) {
        case Action.UpdateState:
            return p[1](rs.state);
        case Action.UpdateSelectedValue:
            return { value: p[1].value };
        case Action.UpdateView:
            return { view: { ...rs.state.view, ...p[1](rs.state.view) } };
        case Action.UpdateGrouping:
            const d = p[1](rs.state);
            return {
                grouping: d.grouping,
                view: {
                    ...rs.state.view,
                    groupingField: d.groupingField,
                    groupingFieldRev: d.groupingFieldRev,
                },
            };
    }
};
export const ListGrid = React.forwardRef(ListGridComponent);
export const ListGridStatic = React.forwardRef(ListGridStaticComponent);
//# sourceMappingURL=list-grid.js.map